// Section 17 "Household rationing": when one member of a household is fed in full and another
// is short, the fed member shares 25% of their surplus (config.rationing.householdSharingFraction)
// unless the household-sharing policy is off. A fed member's surplus is what they received above
// the short members' mean share, and a giver never gives so much that they count as short
// themselves (config.needs.shortThreshold), so sharing "softens the arithmetic a little" rather
// than spreading hunger through the household (A-07). Food only; total food is conserved. Pure.
import type { Person, PersonId } from '../../core/types';
import { EPS } from './plan';

export interface Shared {
  shares: Record<PersonId, number>;
  /** Units moved from fed to short members. */
  shared: number;
}

export function shareWithinHouseholds(
  people: readonly Person[],
  demand: Readonly<Record<PersonId, number>>,
  given: Readonly<Record<PersonId, number>>,
  fraction: number,
  shortBelow: number,
): Shared {
  const shares: Record<PersonId, number> = { ...given };
  let shared = 0;
  const households = new Map<string, Person[]>();
  for (const p of people) {
    if ((demand[p.id] ?? 0) <= 0) continue;
    const list = households.get(p.householdId);
    if (list === undefined) households.set(p.householdId, [p]);
    else list.push(p);
  }
  for (const id of [...households.keys()].sort()) {
    const members = households.get(id) ?? [];
    const fed = members.filter((p) => (shares[p.id] ?? 1) >= 1 - EPS);
    const short = members.filter((p) => (shares[p.id] ?? 1) < shortBelow);
    if (fed.length === 0 || short.length === 0) continue;
    let shortGiven = 0;
    let shortNeed = 0;
    let gap = 0;
    for (const p of short) {
      const d = demand[p.id] ?? 0;
      shortGiven += (shares[p.id] ?? 0) * d;
      shortNeed += d;
      gap += (1 - (shares[p.id] ?? 0)) * d;
    }
    const shortMean = shortNeed > 0 ? shortGiven / shortNeed : 0;
    const gifts = fed.map((p) => {
      const d = demand[p.id] ?? 0;
      const own = shares[p.id] ?? 1;
      const gift = Math.min(fraction * Math.max(0, own - shortMean), Math.max(0, own - shortBelow));
      return { p, d, gift: gift * d };
    });
    const offered = gifts.reduce((a, g) => a + g.gift, 0);
    const moved = Math.min(offered, gap);
    if (moved <= EPS) continue;
    const scale = moved / offered;
    for (const g of gifts) shares[g.p.id] = (shares[g.p.id] ?? 1) - (g.gift * scale) / g.d;
    for (const p of short) {
      const current = shares[p.id] ?? 0;
      shares[p.id] = current + (moved * (1 - current)) / gap;
    }
    shared += moved;
  }
  return { shares, shared };
}
